// Libraries
import React from 'react';

// Supermove
import {Icon, IconSource, PhoneInput, Space, Styled} from '@supermove/components';
import {getQueryName, gql} from '@supermove/graphql';
import {
  ResponsiveType,
  useRef,
  useEffect,
  useModal,
  usePresentationContext,
  ModalType,
} from '@supermove/hooks';
import {ProjectModel, Project, JobModel} from '@supermove/models';
import {useNavigationContext} from '@supermove/navigation';
import {Environment} from '@supermove/sdk';
import {colors} from '@supermove/styles';
import {openNewTab, Phone, withFragment} from '@supermove/utils';

// App
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import DropdownInput from '@shared/design/components/DropdownInput';
import EditableField, {EditableFieldProps} from '@shared/design/components/Field/EditableField';
import Line from '@shared/design/components/Line';
import Text from '@shared/design/components/Text';
import EditBillingValuesModal from '@shared/modules/Accounting/components/Bill/EditBillingValuesModal';
import UpdateValuesForm from '@shared/modules/Billing/forms/UpdateValuesForm';
import useUpdateProjectValue from '@shared/modules/Billing/hooks/useUpdateProjectValue';
import useUpdateValuesMutation from '@shared/modules/Billing/hooks/useUpdateValuesMutation';
import ConversationFooter from '@shared/modules/Communication/components/Footer/ConversationFooter';
import FooterSendEmail from '@shared/modules/Communication/components/Footer/FooterSendEmail';
import ProjectTypeLabel from '@shared/modules/Project/components/widgets/components/ProjectTypeLabel';
import ProjectDescriptionForm from '@shared/modules/Project/forms/ProjectDescriptionForm';
import UpdateProjectSinglePropertyForm from '@shared/modules/Project/forms/UpdateProjectSinglePropertyForm';
import useUpdateProjectClientContactEmailMutation from '@shared/modules/Project/hooks/useUpdateProjectClientContactEmailMutation';
import useUpdateProjectClientContactPhoneNumberMutation from '@shared/modules/Project/hooks/useUpdateProjectClientContactPhoneNumberMutation';
import useUpdateProjectClientNameMutation from '@shared/modules/Project/hooks/useUpdateProjectClientNameMutation';
import useUpdateProjectDescriptionMutation from '@shared/modules/Project/hooks/useUpdateProjectDescriptionMutation';

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
`;

const IconContainer = Styled.View<{size: number}>`
  justify-content: center;
  align-items: center;
  width: ${({size}) => size}px;
  height: ${({size}) => size}px;
`;

const EditableFieldComponent = (props: EditableFieldProps & {responsive: ResponsiveType}) => {
  return (
    <EditableField
      {...props}
      leftIconProps={{
        color: colors.gray.secondary,
        size: props.responsive.desktop ? 12 : 14,
        style: {marginRight: 4},
        ...props?.leftIconProps,
      }}
      valueProps={{empty: 'None', ...props.valueProps}}
      isHorizontal
    />
  );
};

const ProjectDetailButton = ({
  icon,
  text,
  onPress,
}: {
  icon: IconSource;
  text: string;
  onPress: () => void;
}) => {
  return (
    <TertiaryButton
      style={{
        flex: 1,
        justifyContent: 'flex-start',
        paddingVertical: 12,
      }}
      onPress={onPress}
    >
      <IconContainer size={16}>
        <Icon source={icon} size={16} color={colors.blue.interactive} />
      </IconContainer>
      <Space width={8} />
      <Text.Body>{text}</Text.Body>
    </TertiaryButton>
  );
};

const ProjectSizeField = withFragment(
  ({
    project,
    refetch,
    responsive,
  }: {
    project: ProjectModel;
    refetch: () => void;
    responsive: ResponsiveType;
  }) => {
    const originalSize = useRef(project.size);
    const {form, handleSubmitPress, refreshForm} = useUpdateProjectValue({project, refetch});

    // Project size can be updated from other parts of the page, so reset the form if it changes
    useEffect(() => {
      if (project.size !== originalSize.current) {
        originalSize.current = project.size;
        refreshForm();
      }
    }, [project.size, originalSize]);

    if (project.isStorage) {
      return null;
    }

    return (
      <EditableFieldComponent
        leftIcon={Icon.HouseBuilding}
        leftIconTooltip={'Project size'}
        valueProps={{value: project.size}}
        inputProps={{
          ...form,
          name: 'displayValuesForm.valueForms.0.stringValue',
          component: DropdownInput,
          input: {
            placeholder: 'Enter project size',
            options: project.valueByIdentifier.variable.variableOptions.map(({label, value}) => ({
              label,
              value,
            })),
            setFieldValue: form.setFieldValue,
            isPortaled: true,
            style: {width: '100%'},
          },
        }}
        responseToasts={{displayName: 'Project size'}}
        handleSubmit={handleSubmitPress}
        responsive={responsive}
      />
    );
  },
  gql`
    ${useUpdateProjectValue.fragment}
    fragment ProjectOverviewFields_ProjectSize on Project {
      id
      size
      valueByIdentifier(identifier: "PROJECT_SIZE") {
        id
        variable {
          id
          variableOptions {
            label
            value
          }
        }
        ...useUpdateProjectValue_Value
      }
    }
  `,
);

const ReadOnlyProjectSizeField = withFragment(
  ({project, responsive}: {project: ProjectModel; responsive: ResponsiveType}) => {
    if (project.isStorage) {
      return null;
    }

    return (
      <EditableFieldComponent
        isDisabled
        leftIcon={Icon.HouseBuilding}
        leftIconTooltip={'Project size'}
        valueProps={{value: project.size}}
        responsive={responsive}
      />
    );
  },
  gql`
    ${useUpdateProjectValue.fragment}
    fragment ProjectOverviewFields_ProjectSize on Project {
      id
      size
    }
  `,
);

const BillingValuesModal = ({
  project,
  refetch,
  editBillingValuesModal,
}: {
  project: ProjectModel;
  refetch: () => void;
  editBillingValuesModal: ModalType;
}) => {
  const updateValuesForm = UpdateValuesForm.edit(project);
  const updateValuesMutation = useUpdateValuesMutation({
    updateValuesForm,
    onSuccess: () => {
      editBillingValuesModal.handleClose();
      refetch();
    },
    onError: (error) => console.log({error}),
  });

  return (
    <EditBillingValuesModal
      key={editBillingValuesModal.key}
      isOpen={editBillingValuesModal.isOpen}
      handleClose={editBillingValuesModal.handleClose}
      updateValuesForm={updateValuesMutation.form}
      handleSubmit={updateValuesMutation.handleSubmit}
      projectUuid={project.uuid}
    />
  );
};

const CalendarField = withFragment(
  ({
    project,
    responsive,
    editableJob,
  }: {
    project: ProjectModel;
    responsive: ResponsiveType;
    editableJob?: JobModel;
  }) => {
    const officeAppEnabled = project.organization.isOfficeAppEnabled;

    return (
      <React.Fragment>
        {/* We probably want to create an inline version of date editing, sales without copilot can't edit dates */}
        <EditableFieldComponent
          leftIcon={Icon.Calendar}
          isDisabled={project.isStorage || !editableJob || !officeAppEnabled}
          leftIconTooltip={'Start & End Date'}
          valueProps={{value: Project.getDate(project)}}
          handleEditPress={() => {
            if (editableJob) {
              openNewTab(
                `${Environment.getAppUrl({useLocalUrl: true})}/projects/${project.uuid}/edit/jobs?jobUuid=${editableJob.uuid}`,
              );
            }
          }}
          responsive={responsive}
        />
        <Space height={8} />
      </React.Fragment>
    );
  },
  gql`
    ${Project.getDate.fragment}

    fragment ProjectOverviewFields_Calendar_Project on Project {
      id
      uuid
      isStorage
      organization {
        id
        isOfficeAppEnabled
      }
      ...Project_getDate
    }

    fragment ProjectOverviewFields_Calendar_Job on Job {
      id
      uuid
    }
  `,
);

const ProjectDataFields = withFragment(
  ({
    project,
    isWidget,
    responsive,
    refetch,
    handleOpenProjectLocations,
    handleClose,
  }: {
    project: ProjectModel;
    isWidget: boolean;
    responsive: ResponsiveType;
    refetch: () => void;
    handleOpenProjectLocations?: () => void;
    handleClose?: () => void;
  }) => {
    const editableJob = project.allJobsExcludingChildJobs?.find((job) => !job.isCancelled);
    const {navigator} = useNavigationContext();
    const {appName} = usePresentationContext();
    const editBillingValuesModal = useModal({name: 'Edit Billing Values Modal Project Overview'});
    const {isOfficeAppEnabled} = project.organization;

    return (
      <React.Fragment>
        {isWidget ? (
          <CalendarField project={project} responsive={responsive} editableJob={editableJob} />
        ) : (
          <React.Fragment>
            <ProjectDetailButton
              icon={Icon.Calendar}
              text={Project.getDate(project)}
              onPress={() => {
                if (editableJob) {
                  const baseUrl =
                    appName === 'Manager' ? '' : Environment.getAppUrl({useLocalUrl: true});
                  navigator.push(
                    `${baseUrl}/projects/${project.uuid}/edit/jobs?jobUuid=${editableJob.uuid}`,
                  );
                }
                handleClose?.();
              }}
            />
            <Line />
          </React.Fragment>
        )}
        {isWidget ? (
          <React.Fragment>
            {project.organization.isOfficeAppEnabled ? (
              <ProjectSizeField project={project} refetch={refetch} responsive={responsive} />
            ) : (
              <ReadOnlyProjectSizeField project={project} responsive={responsive} />
            )}
            <Space height={8} />
          </React.Fragment>
        ) : project.size ? (
          <React.Fragment>
            <ProjectDetailButton
              icon={Icon.HouseBuilding}
              text={project.size}
              onPress={() => {
                editBillingValuesModal.handleOpen();
                handleClose?.();
              }}
            />
            <Line />
          </React.Fragment>
        ) : null}
        {isWidget ? (
          isOfficeAppEnabled ? (
            <React.Fragment>
              <EditableFieldComponent
                isDisabled={!project.organization.isOfficeAppEnabled}
                leftIcon={Icon.WeightHanging}
                leftIconTooltip={'Weight'}
                valueProps={{value: `${project.weight} lbs`}}
                handleEditPress={editBillingValuesModal.handleOpen}
                responsive={responsive}
              />
              <Space height={8} />
            </React.Fragment>
          ) : null
        ) : (
          <React.Fragment>
            <ProjectDetailButton
              icon={Icon.WeightHanging}
              text={`${project.weight} lbs`}
              onPress={editBillingValuesModal.handleOpen}
            />
            <Line />
          </React.Fragment>
        )}
        {isWidget ? (
          project.isStorage || !isOfficeAppEnabled ? null : (
            <React.Fragment>
              <EditableFieldComponent
                isDisabled={!project.organization.isOfficeAppEnabled}
                leftIcon={Icon.RulerTriangle}
                leftIconTooltip={'Volume'}
                valueProps={{value: `${project.volume} ft³`}}
                handleEditPress={editBillingValuesModal.handleOpen}
                responsive={responsive}
              />
              <Space height={8} />
            </React.Fragment>
          )
        ) : (
          <React.Fragment>
            <ProjectDetailButton
              icon={Icon.RulerTriangle}
              text={`${project.volume} ft³`}
              onPress={editBillingValuesModal.handleOpen}
            />
            <Line />
          </React.Fragment>
        )}
        {isWidget ? (
          project.isStorage || !isOfficeAppEnabled ? null : (
            <React.Fragment>
              <EditableFieldComponent
                isDisabled={!handleOpenProjectLocations}
                leftIcon={Icon.MapPin}
                leftIconTooltip={'Origin & Destination'}
                valueProps={{value: Project.getLocation(project)}}
                handleEditPress={handleOpenProjectLocations}
                responsive={responsive}
              />
              <Space height={8} />
            </React.Fragment>
          )
        ) : null}
        {project.organization.isOfficeAppEnabled ? (
          <BillingValuesModal
            project={project}
            refetch={refetch}
            editBillingValuesModal={editBillingValuesModal}
          />
        ) : null}
      </React.Fragment>
    );
  },
  gql`
    ${Project.getLocation.fragment}
    ${Project.getDate.fragment}
    ${ProjectSizeField.fragment}
    ${UpdateValuesForm.edit.fragment}
    ${CalendarField.fragment}

    fragment ProjectOverviewFields_ProjectData on Project {
      id
      uuid
      description
      weight
      volume
      size
      isStorage
      ...ProjectOverviewFields_ProjectSize
      allJobsExcludingChildJobs {
        id
        uuid
        isCancelled
        ...ProjectOverviewFields_Calendar_Job
      }
      ...Project_getDate
      ...Project_getLocation
      ...useUpdateProjectValue_Project
      ...UpdateValuesForm_edit
      ...ProjectOverviewFields_Calendar_Project
    }
  `,
);

const ProjectTypeField = withFragment(
  ({project}: {project: ProjectModel}) => {
    return (
      <Row style={{marginLeft: 4}}>
        <ProjectTypeLabel.Dot color={project.projectType.color} tooltip={'Project type'} />
        <Space width={12} />
        <Text.Body>{project.projectType.name}</Text.Body>
      </Row>
    );
  },
  gql`
    fragment ProjectOverviewFields_ProjectType on Project {
      id
      projectType {
        id
        color
        name
      }
    }
  `,
);

const DescriptionField = withFragment(
  ({project, onSuccess}: {project: ProjectModel; onSuccess: () => void}) => {
    const {form, handleSubmit} = useUpdateProjectDescriptionMutation({
      projectDescriptionForm: ProjectDescriptionForm.toEdit({
        projectId: project.id,
        description: project.description,
      }),
      onSuccess,
      onError: (errors) => console.log({errors}),
    });

    return (
      <EditableField
        labelProps={{children: 'Description'}}
        valueProps={{value: project.description, empty: 'None'}}
        inputProps={{
          ...form,
          name: 'projectDescriptionForm.description',
          input: {multiline: true, style: {minHeight: 136, paddingTop: 8}},
        }}
        handleSubmit={handleSubmit}
        containerStyle={{marginVertical: 8}}
        responseToasts={{displayName: 'Description'}}
      />
    );
  },
  gql`
    fragment ProjectOverviewFields_Description on Project {
      id
      description
    }
  `,
);

const ClientNameField = withFragment(
  ({
    project,
    refetch,
    responsive,
  }: {
    project: ProjectModel;
    refetch: () => void;
    responsive: ResponsiveType;
  }) => {
    const {form, handleSubmit} = useUpdateProjectClientNameMutation({
      updateProjectSinglePropertyForm: UpdateProjectSinglePropertyForm.edit({
        projectId: project.id,
        property: 'name',
        stringValue: project.client.primaryContact?.fullName,
      }),
      onSuccess: refetch,
      onError: (errors) => console.log({errors}),
    });
    return (
      <EditableFieldComponent
        leftIcon={Icon.User}
        leftIconTooltip={'Customer name'}
        valueProps={{value: project.client.primaryContact?.fullName}}
        inputProps={{
          ...form,
          name: 'updateProjectSinglePropertyForm.stringValue',
          input: {placeholder: 'Enter customer name'},
        }}
        handleSubmit={handleSubmit}
        responseToasts={{displayName: 'Customer name'}}
        responsive={responsive}
      />
    );
  },
  gql`
    fragment ProjectOverviewFields_ClientName on Project {
      id
      client {
        id
        primaryContact {
          id
          fullName
        }
      }
    }
  `,
);

const ClientEmailField = withFragment(
  ({
    project,
    refetch,
    responsive,
  }: {
    project: ProjectModel;
    refetch: () => void;
    responsive: ResponsiveType;
  }) => {
    const {form, handleSubmit} = useUpdateProjectClientContactEmailMutation({
      updateProjectSinglePropertyForm: UpdateProjectSinglePropertyForm.edit({
        projectId: project.id,
        property: 'email',
        stringValue: project.client.primaryContact.email,
      }),
      onSuccess: refetch,
      onError: ({errors}) => console.log(errors),
      refetchQueries: [getQueryName(ConversationFooter.query), getQueryName(FooterSendEmail.query)],
    });
    return (
      <EditableFieldComponent
        leftIcon={Icon.Envelope}
        leftIconTooltip={'Email'}
        valueProps={{value: project.client.primaryContact.email}}
        inputProps={{
          ...form,
          name: 'updateProjectSinglePropertyForm.stringValue',
          input: {placeholder: 'Enter email'},
        }}
        handleSubmit={handleSubmit}
        responseToasts={{displayName: 'Email'}}
        responsive={responsive}
      />
    );
  },
  gql`
    fragment ProjectOverviewFields_ClientEmail on Project {
      id
      client {
        id
        primaryContact {
          id
          email
        }
      }
    }
  `,
);

const ClientPhoneNumberField = withFragment(
  ({
    project,
    refetch,
    responsive,
  }: {
    project: ProjectModel;
    refetch: () => void;
    responsive: ResponsiveType;
  }) => {
    const {form, handleSubmit} = useUpdateProjectClientContactPhoneNumberMutation({
      updateProjectSinglePropertyForm: UpdateProjectSinglePropertyForm.edit({
        projectId: project.id,
        property: 'phoneNumber',
        stringValue: Phone.strip(project.client.primaryContact.phoneNumber),
      }),
      onSuccess: refetch,
      onError: (errors) => console.log({errors}),
    });
    return (
      <EditableFieldComponent
        leftIcon={Icon.Phone}
        leftIconTooltip={'Phone number'}
        valueProps={{value: Phone.display(project.client.primaryContact.phoneNumber)}}
        inputProps={{
          ...form,
          name: 'updateProjectSinglePropertyForm.stringValue',
          component: PhoneInput,
          input: {
            placeholder: 'Enter phone number',
            setFieldValue: form.setFieldValue,
          },
        }}
        handleSubmit={handleSubmit}
        responseToasts={{displayName: 'Phone number'}}
        responsive={responsive}
      />
    );
  },
  gql`
    fragment ProjectOverviewFields_ClientPhoneNumber on Project {
      id
      client {
        id
        primaryContact {
          id
          phoneNumber
        }
      }
    }
  `,
);

const CompanyField = withFragment(
  ({project, responsive}: {project: ProjectModel; responsive: ResponsiveType}) => {
    return (
      <EditableFieldComponent
        leftIcon={Icon.Building}
        leftIconTooltip={'Company'}
        valueProps={{value: project.organization.name}}
        isDisabled
        responsive={responsive}
      />
    );
  },
  gql`
    fragment ProjectOverviewFields_Company on Project {
      id
      organization {
        id
        name
        isOfficeAppEnabled
      }
    }
  `,
);
const ProjectOverviewFields = {
  Company: CompanyField,
  ClientName: ClientNameField,
  ClientEmail: ClientEmailField,
  ClientPhoneNumber: ClientPhoneNumberField,
  ProjectType: ProjectTypeField,
  Description: DescriptionField,
  ProjectSize: ProjectSizeField,
  ProjectData: ProjectDataFields,
};

export default ProjectOverviewFields;
